import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";
import { useDispatch } from "react-redux";
import {
  loginSuccess,
  logout,
  setTeacherData,
  setStudentData,
  clearTeacher,
  clearStudent,
} from "../store";
import { API_BASE } from "../config/api.js";
import { isAdminEmail } from "../constants/admins.js";
import { getTeacherAuthRole } from "../utils/teacherStatus";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);

      if (!firebaseUser) {
        setUser(null);
        dispatch(logout());
        dispatch(clearTeacher());
        dispatch(clearStudent());
        setLoading(false);
        return;
      }

      setUser(firebaseUser);

      fetch(`${API_BASE}/health`).catch(() => {});

      if (isAdminEmail(firebaseUser.email)) {
        dispatch(loginSuccess({ uid: firebaseUser.uid, role: "admin" }));
        dispatch(clearTeacher());
        dispatch(clearStudent());
        setLoading(false);
        return;
      }

      const teacherSnap = await getDoc(doc(db, "teachers", firebaseUser.uid));

      if (teacherSnap.exists()) {
        const teacherData = teacherSnap.data();
        dispatch(
          loginSuccess({
            uid: firebaseUser.uid,
            role: getTeacherAuthRole(teacherData),
          })
        );
        dispatch(setTeacherData(teacherData));
        dispatch(clearStudent());
        setLoading(false);
        return;
      }

      const studentSnap = await getDoc(doc(db, "students", firebaseUser.uid));

      if (studentSnap.exists()) {
        dispatch(loginSuccess({ uid: firebaseUser.uid, role: "student" }));
        dispatch(setStudentData(studentSnap.data()));
        dispatch(clearTeacher());
        setLoading(false);
        return;
      }

      dispatch(logout());
      setLoading(false);
    });

    return () => unsub();
  }, [dispatch]);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
